import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { checkCartInventory, useShopifyCheckout, fetchShopifyProduct } from '@/entities/shopify';
import { useSubscribeCartStoreShallow, useCartStoreActions } from '../hooks/cartStoreHooks';
import { cartStoreSelectors } from '../selectors';
import CartItem from './CartItem/CartItem';
import CartTotalPrice from './CartTotalPrice';

const CartDrawer = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  const itemSlugs = useSubscribeCartStoreShallow(cartStoreSelectors.itemSlugs);
  const cartItems = useSubscribeCartStoreShallow(cartStoreSelectors.items);
  const { redirectToCheckout, isLoading: isRedirecting } = useShopifyCheckout();
  const [isCheckingInventory, setIsCheckingInventory] = useState(false);
  const toast = useToast();
  const router = useRouter();
  const { clearCart } = useCartStoreActions();

  const isLoading = isCheckingInventory || isRedirecting;

  const handleCheckout = async () => {
    setIsCheckingInventory(true);

    try {
      const itemsToCheckPromises = cartItems.map(async (item) => {
        const baseSlug = item.slug.replace(/-(?:white|black)$/, '');
        const color = item.color || 'white';

        const shopifyProduct = await fetchShopifyProduct(baseSlug);

        if (!shopifyProduct) {
          console.error(`No Shopify product found for handle: ${baseSlug}`);
          return null;
        }

        const variant = shopifyProduct.variants.find((v) =>
          v.selectedOptions.some(
            (opt) =>
              opt.name.toLowerCase() === 'color' && opt.value.toLowerCase() === color.toLowerCase(),
          ),
        );

        if (!variant) {
          console.error(`No variant found for ${baseSlug} with color ${color}`);
          return null;
        }

        return {
          variantId: variant.id,
          quantity: item.quantity,
          slug: item.slug,
        };
      });

      const itemsToCheck = (await Promise.all(itemsToCheckPromises)).filter(
        (item): item is NonNullable<typeof item> => item !== null,
      );

      if (itemsToCheck.length === 0) {
        throw new Error(t('store.cart.errors.noValidProducts'));
      }

      const { allInStock } = await checkCartInventory(itemsToCheck);

      if (!allInStock) {
        onClose();
        toast({
          title: t('store.cart.errors.outOfStock'),
          description: t('store.cart.errors.redirectingToCheckout'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });

        router.push('/store/checkout');
        return;
      }

      setIsCheckingInventory(false);
      await redirectToCheckout(cartItems);
      clearCart();
    } catch (error) {
      console.error('Checkout error details:', error);

      let errorMessage = t('store.cart.errors.checkoutFailed');

      if (error instanceof Error) {
        if (error.message.includes('No valid Shopify products')) {
          errorMessage = t('store.cart.errors.productsNotAvailable');
        } else if (error.message.includes('variant ID')) {
          errorMessage = t('store.cart.errors.productInfoMissing');
        } else if (error.message.includes('Failed to create cart')) {
          errorMessage = t('store.cart.errors.connectionFailed');
        }
      }

      toast({
        title: 'Checkout Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsCheckingInventory(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement='right'
      size={{ base: 'full', md: 'sm' }}
      onClose={onClose}
    >
      <DrawerOverlay />
      <DrawerContent maxH='dvh100'>
        <DrawerCloseButton />
        <DrawerHeader>{t('store.cart.yourCart')}</DrawerHeader>

        <DrawerBody>
          <VStack spacing={3}>
            {itemSlugs.map((itemSlug) => (
              <CartItem
                key={itemSlug}
                itemSlug={itemSlug}
              />
            ))}
          </VStack>
        </DrawerBody>

        <DrawerFooter
          flexDirection='column'
          gap={3}
        >
          <CartTotalPrice />
          <Button
            bgColor='secondary.dark'
            colorScheme='secondary'
            disabled={itemSlugs.length === 0}
            isLoading={isLoading}
            loadingText='Redirecting...'
            w='full'
            onClick={handleCheckout}
          >
            {t('store.cart.checkout')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default CartDrawer;
